import { useState } from 'react';
import { i18nDynamic } from 'shared/i18n';
import { getRecentFailedRequests } from 'shared/lib/request';

const t = (key: string, fallback: string) => {
  const label = i18nDynamic(key);
  return label === key ? fallback : label;
};

const caughtComponentStacks = new WeakMap<object, string>();

export const rememberComponentStack = (error: unknown, componentStack?: string | null) => {
  if (componentStack && typeof error === 'object' && error !== null) {
    caughtComponentStacks.set(error, componentStack);
  }
};

interface ErrorFallbackProps {
  error?: Error | null;
  componentStack?: string | null;
}

const ErrorFallback = ({ error, componentStack }: ErrorFallbackProps) => {
  const [copied, setCopied] = useState(false);

  const getDeveloperInfo = () => {
    const stack = componentStack ?? (error ? caughtComponentStacks.get(error) : undefined);
    return JSON.stringify(
      {
        error: error?.message,
        componentStack: stack ? stack.split('\n').slice(0, 30).join('\n') : undefined,
        stack: error?.stack ? error.stack.split('\n').slice(0, 20).join('\n') : undefined,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        recentFailedRequests: getRecentFailedRequests(),
      },
      null,
      2
    );
  };

  const handleCopy = () => {
    const info = getDeveloperInfo();

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(info).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = info;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
      <div className="text-center" style={{ maxWidth: '500px' }}>
        <div className="mb-4">
          <i className="fa-regular fa-triangle-exclamation" style={{ fontSize: '3rem', color: '#f0ad4e' }} />
        </div>

        <h2 className="mb-3">{t('page:unexpected.error.header', 'Something went wrong')}</h2>

        <p className="text-muted mb-4">{t('page:unexpected.error.info', 'If this keeps happening, contact Xplir Support.')}</p>

        <div className="d-flex justify-content-center gap-2 mb-3">
          <a href="/client/" className="btn btn-primary">
            {t('page:unexpected.error.go.home', 'Go to homepage')}
          </a>
          <a href={window.location.href} className="btn btn-secondary">
            {t('page:unexpected.error.try.again', 'Try again')}
          </a>
        </div>

        <button className="btn btn-outline-dark btn-sm" onClick={handleCopy}>
          {copied ? t('page:unexpected.error.copied', 'Copied!') : t('page:unexpected.error.copy.info', 'Copy developer info')}
        </button>
      </div>
    </div>
  );
};

export default ErrorFallback;
